import type { PetSheet } from "@highmarch/app";
import { icon } from "../iconsKit";

/**
 * A hub pet's tap target (Pets v2/v3, docs/PETS.md): a round, see-through button that rides over the pet on the lawn
 * (the scene's `{ pet }` anchor, render/hubScene.ts, read every frame — the pet trots about), with a small "Lv 7" tag that
 * wears a gold arrow while an upgrade is ready, an hourglass while one runs. A tap opens the pet sheet. Hidden while
 * no pet is on the lawn (none caught, or it stays home). Presentation only; the hub widget in features/pets.ts mounts it.
 */
export const HUB_PET_TAP = {
  /** The target's size (CSS px): generous, a pet is small on a phone. */
  size: 64,
  /** The anchor is just over the pet's back; the target's centre sits this far below it (px). */
  drop: 18,
  /** Two pets' tags closer than this (px across, px down) overlap: the one not coming along drops its tag (a tap still opens it). */
  tagApart: [58, 22] as readonly [number, number],
} as const;

/** The tag's markup for the sheet's state. */
export function hubPetTagHtml(s: PetSheet): string {
  const u = s.upgrade.state, ready = u === "ready" && s.upgrade.affordable;
  const mark = u === "running" || u === "queued" ? icon("clock", 11) : ready ? `<i class="hp-up" aria-hidden="true">▲</i>` : "";
  return `<b class="disp">Lv ${s.level}</b>${mark}`;
}

export class HubPetTap {
  readonly el: HTMLButtonElement;
  private raf = 0;
  private gone = false;
  /** A pet is on the lawn (the last update's sheet), so the frame loop need not build the sheet. */
  private shown = false;

  constructor(layer: HTMLElement, private readonly anchor: () => { x: number; y: number } | null, private readonly sheet: () => PetSheet | null, open: () => void, petId = "") {
    this.el = document.createElement("button");
    this.el.type = "button";
    this.el.className = "hubpet hidden";
    this.el.dataset.pet = petId;
    this.el.style.width = this.el.style.height = `${HUB_PET_TAP.size}px`;
    this.el.innerHTML = `<span class="hp-tag"></span>`;
    this.el.onclick = open;
    layer.append(this.el);
    this.update();
    this.raf = requestAnimationFrame(this.frame);
  }

  /** The tag's level and state (the hub tick, and after a change). */
  update(): void {
    const s = this.sheet();
    this.shown = s !== null;
    if (!s) { this.el.classList.add("hidden"); return; }
    this.el.setAttribute("aria-label", `${s.pet.name}, level ${s.level}: open its sheet`);
    const tag = this.el.querySelector<HTMLElement>(".hp-tag")!, html = hubPetTagHtml(s);
    if (tag.innerHTML !== html) tag.innerHTML = html;
    this.el.classList.toggle("ready", s.upgrade.state === "ready" && s.upgrade.affordable);
    this.el.classList.toggle("on", s.equipped);
  }

  /** Where it was drawn this frame (null: off the lawn). */
  at: { x: number; y: number } | null = null;
  get on(): boolean { return this.el.classList.contains("on"); }
  /** Its tag shown or dropped (HubPetTaps keeps tags from overlapping). */
  tagShown(shown: boolean): void { this.el.classList.toggle("notag", !shown); }

  private frame = (): void => {
    if (this.gone) return;
    // M44: a target whose hub went without disposing it stops its own loop rather than run under every later screen
    if (!this.el.isConnected) { this.dispose(); return; }
    this.raf = requestAnimationFrame(this.frame);
    const at = this.shown ? this.anchor() : null;
    this.at = at;
    this.el.classList.toggle("hidden", at === null);
    if (at) this.el.style.transform = `translate(${Math.round(at.x - HUB_PET_TAP.size / 2)}px, ${Math.round(at.y + HUB_PET_TAP.drop - HUB_PET_TAP.size / 2)}px)`;
  };

  dispose(): void { this.gone = true; cancelAnimationFrame(this.raf); this.el.remove(); }
}

/**
 * Every pet on the lawn a tap target (Pets v3: they all wander there, render/views/petHubPack.ts): one HubPetTap per pet
 * had, made and dropped as pets are caught (`update`, the hub's 1 s tick). `sheetOf(id)` builds a pet's sheet,
 * `anchorOf(id)` finds it on screen, `open(id)` opens its sheet.
 */
export class HubPetTaps {
  private readonly taps = new Map<string, HubPetTap>();
  private raf = 0;

  constructor(private readonly layer: HTMLElement, private readonly owned: () => readonly string[], private readonly anchorOf: (id: string) => { x: number; y: number } | null,
              private readonly sheetOf: (id: string) => PetSheet | null, private readonly open: (id: string) => void) {
    this.update();
    this.raf = requestAnimationFrame(this.declutter);
  }

  /** Each frame: tags that would overlap give way, the one coming along first, then roster order. */
  private declutter = (): void => {
    this.raf = requestAnimationFrame(this.declutter);
    const [ax, ay] = HUB_PET_TAP.tagApart, kept: { x: number; y: number }[] = [];
    const order = [...this.taps.values()].sort((a, b) => Number(b.on) - Number(a.on));
    for (const t of order) {
      const at = t.at, clear = at !== null && kept.every((k) => Math.abs(k.x - at.x) >= ax || Math.abs(k.y - at.y) >= ay);
      t.tagShown(clear);
      if (clear && at) kept.push(at);
    }
  };

  update(): void {
    if (this.disposed) return;                // M44: a late tick on a disposed set must not make tap targets nobody owns
    const ids = this.owned();
    for (const [id, t] of this.taps) if (!ids.includes(id)) { t.dispose(); this.taps.delete(id); }
    for (const id of ids) {
      const t = this.taps.get(id);
      if (t) t.update();
      else this.taps.set(id, new HubPetTap(this.layer, () => this.anchorOf(id), () => this.sheetOf(id), () => this.open(id), id));
    }
  }

  dispose(): void { this.disposed = true; cancelAnimationFrame(this.raf); for (const t of this.taps.values()) t.dispose(); this.taps.clear(); }
  private disposed = false;
}
